// Client-side graph executor: topological sort, run each node, wire outputs → inputs.
// Phase 0: sequential, stop-on-first-error. Phase 3 will swap this for backend execution.

import { type Node, type Edge } from '@xyflow/react';
import { aiModelsApi } from '@/lib/ai-models-api';
import { api } from '@/lib/api';
import { usePipelineStore } from './store';
import { EXECUTORS, type ExecContext } from './executors';
import { NODE_SPECS } from './registry';
import { unconfiguredReason } from './validate';
import { refDependencyEdges } from './refs';
import { type NodeStatus } from './types';

/** Labels of input nodes that still need configuration (for a pre-run hint). */
function unconfiguredNodeLabels(): string[] {
  const { nodes, edges } = usePipelineStore.getState();
  return nodes
    .filter((n) => unconfiguredReason(n, edges))
    .map((n) => NODE_SPECS[n.type as string]?.label ?? n.type ?? 'node');
}

/** Kahn topological sort. Returns null if the graph contains a cycle. */
function topoSort(nodes: Node[], edges: Edge[]): Node[] | null {
  const indeg = new Map<string, number>();
  const adj = new Map<string, string[]>();
  nodes.forEach((n) => {
    indeg.set(n.id, 0);
    adj.set(n.id, []);
  });
  edges.forEach((e) => {
    if (!indeg.has(e.source) || !indeg.has(e.target)) return;
    adj.get(e.source)!.push(e.target);
    indeg.set(e.target, (indeg.get(e.target) ?? 0) + 1);
  });

  const queue: string[] = nodes.filter((n) => (indeg.get(n.id) ?? 0) === 0).map((n) => n.id);
  const order: string[] = [];
  while (queue.length) {
    const id = queue.shift()!;
    order.push(id);
    for (const m of adj.get(id) ?? []) {
      indeg.set(m, (indeg.get(m) ?? 0) - 1);
      if ((indeg.get(m) ?? 0) === 0) queue.push(m);
    }
  }
  if (order.length !== nodes.length) return null; // cycle
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return order.map((id) => byId.get(id)!);
}

/** Resolve a node's inputs from upstream outputs: inputs[targetHandle] = output(source)[sourceHandle].
 *  Shared by the run engine and the live preview so both wire the graph identically. */
export function resolveNodeInputs(
  nodeId: string,
  edges: Edge[],
  getOutput: (sourceId: string) => Record<string, unknown> | undefined,
): Record<string, unknown> {
  const inputs: Record<string, unknown> = {};
  for (const e of edges) {
    if (e.target !== nodeId || !e.targetHandle || !e.sourceHandle) continue;
    const srcOut = getOutput(e.source);
    if (srcOut) inputs[e.targetHandle] = srcOut[e.sourceHandle];
  }
  return inputs;
}

/** Stable JSON (sorted keys) for deterministic cache keys. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(',')}}`;
}

/** Small, fast string hash (djb2). */
function hash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export async function runPipeline(): Promise<{ ok: boolean; error?: string }> {
  const { nodes, edges, resetRuntime, setRuntime, setRunning } = usePipelineStore.getState();
  if (!nodes.length) return { ok: false, error: 'Graph is empty' };

  const missing = unconfiguredNodeLabels();
  if (missing.length) return { ok: false, error: `Configure first: ${missing.join(', ')}` };

  // Order by real edges + implicit {nodeId} prompt-reference dependencies.
  const allEdges = [...edges, ...refDependencyEdges(nodes)];
  const sorted = topoSort(nodes, allEdges);
  if (!sorted) return { ok: false, error: 'Graph has a cycle — remove a connection' };

  resetRuntime();
  setRunning(true);
  const outputs: Record<string, Record<string, unknown>> = {};
  const ctx: ExecContext = { aiModels: aiModelsApi, api, outputs };
  const keys: Record<string, string> = {};

  try {
    for (const node of sorted) {
      const exec = EXECUTORS[node.type as string];
      if (!exec) continue; // unknown node type — skip gracefully

      // Resolve inputs from upstream outputs.
      const inputs = resolveNodeInputs(node.id, allEdges, (src) => outputs[src]);
      const inputKeyParts: string[] = [];
      for (const e of allEdges) {
        if (e.target !== node.id || !e.targetHandle) continue;
        inputKeyParts.push(`${e.targetHandle}<-${keys[e.source] ?? '?'}.${e.sourceHandle}`);
      }

      // Cache key = node type + config + upstream node keys (transitively captures changes).
      const nodeKey = hash(
        `${node.type}|${stableStringify(node.data ?? {})}|${inputKeyParts.sort().join(',')}`,
      );
      keys[node.id] = nodeKey;

      // Skip-unchanged: reuse cached output if the key matches the last run.
      const title = (node.data as { title?: string })?.title;
      const cached = usePipelineStore.getState().cache[node.id];
      if (cached && cached.key === nodeKey) {
        outputs[node.id] = cached.output;
        if (title) outputs[title] = cached.output;
        setRuntime(node.id, { status: 'cached', output: cached.output });
        continue;
      }

      setRuntime(node.id, { status: 'running', error: undefined });
      try {
        const out = (await exec(inputs, node.data ?? {}, ctx)) ?? {};
        outputs[node.id] = out;
        if (title) outputs[title] = out;
        usePipelineStore.getState().setCacheEntry(node.id, { key: nodeKey, output: out });
        setRuntime(node.id, { status: 'done', output: out });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setRuntime(node.id, { status: 'error', error: message });
        return { ok: false, error: `${node.type}: ${message}` };
      }
    }
    return { ok: true };
  } finally {
    setRunning(false);
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Map a server run's node_results into per-node runtime (status + displayable output). */
export function applyNodeResults(
  results: Record<
    string,
    { status: string; image?: string; video?: string; text?: string; info?: string; ms?: number; error?: string }
  >,
  setRuntime: (id: string, rt: { status: NodeStatus; output?: Record<string, unknown>; error?: string }) => void,
) {
  for (const [id, r] of Object.entries(results)) {
    const status = (r.status as NodeStatus) ?? 'idle';
    const output: Record<string, unknown> = {};
    if (r.image) output.image = api.getImageUrl(r.image);
    if (r.video) output.video = api.getVideoUrl(r.video);
    if (r.text != null) output.text = r.text;
    if (r.info != null) output._info = r.info;
    if (r.ms != null) output._ms = r.ms;
    setRuntime(id, { status, output, error: r.error });
  }
}

type Setter = (id: string, rt: { status: NodeStatus; output?: Record<string, unknown>; error?: string }) => void;
type RunResult = { ok: boolean; error?: string };

/** Stream a run's progress over WebSocket. Rejects (so the caller can fall back to polling). */
function streamRunViaWs(runId: number, setRuntime: Setter): Promise<RunResult> {
  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    try {
      ws = new WebSocket(api.wsUrl(`/ws/pipelines/runs/${runId}`));
    } catch (e) {
      reject(e);
      return;
    }
    let settled = false;
    let gotMessage = false;
    const done = (r: RunResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        ws.close();
      } catch {
        /* ignore */
      }
      resolve(r);
    };
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        reject(new Error('ws timeout'));
      }
    }, 30 * 60 * 1000);

    ws.onmessage = (ev) => {
      gotMessage = true;
      let m: { status?: string; node_results?: Record<string, never>; error?: string };
      try {
        m = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (m.error && !m.status) return done({ ok: false, error: m.error });
      if (m.node_results) applyNodeResults(m.node_results, setRuntime);
      if (m.status === 'completed') done({ ok: true });
      else if (m.status === 'failed') done({ ok: false, error: m.error ?? 'Run failed' });
    };
    ws.onerror = () => {
      if (!settled && !gotMessage) {
        settled = true;
        clearTimeout(timer);
        reject(new Error('ws error'));
      }
    };
    ws.onclose = () => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        reject(new Error('ws closed early'));
      }
    };
  });
}

/** Polling fallback when WebSocket is unavailable. */
async function pollRun(runId: number, setRuntime: Setter): Promise<RunResult> {
  const deadline = Date.now() + 30 * 60 * 1000;
  while (Date.now() < deadline) {
    const r = await api.getPipelineRun(runId);
    applyNodeResults(r.node_results ?? {}, setRuntime);
    if (r.status === 'completed') return { ok: true };
    if (r.status === 'failed') return { ok: false, error: r.error ?? 'Run failed' };
    await sleep(1000);
  }
  return { ok: false, error: 'Run timed out' };
}

/** Run the graph server-side: submit it, then stream progress over WS (poll fallback). */
export async function runPipelineOnServer(): Promise<RunResult> {
  const { nodes, edges, resetRuntime, setRuntime, setRunning } = usePipelineStore.getState();
  if (!nodes.length) return { ok: false, error: 'Graph is empty' };

  const missing = unconfiguredNodeLabels();
  if (missing.length) return { ok: false, error: `Configure first: ${missing.join(', ')}` };

  resetRuntime();
  setRunning(true);
  try {
    const graphJson = JSON.stringify({ version: 1, nodes, edges });
    const run = await api.createPipelineRun(graphJson);
    try {
      return await streamRunViaWs(run.id, setRuntime);
    } catch {
      return await pollRun(run.id, setRuntime); // WS unavailable → poll
    }
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  } finally {
    setRunning(false);
  }
}
